import React, { useState, useEffect, useCallback } from 'react';
import GridSidebar from './GridSidebar';
import CluesPanel from './CluesPanel';
import WordDesk from './WordDesk';
import ClueStepper from './ClueStepper';
import EmojiPickerDialog from './EmojiPickerDialog';
import { buildPastClueIndex } from '../../services/wordInfoService';
import { moveCrosswordData } from './CrosswordMover';
import { hyphenateFinnish } from '../../hyphenationService';
import { formatDate } from '../../utils/dateUtils';
import EditorHeader from './EditorHeader';
import CrosswordGrid from './CrosswordGrid';
import { useCellArrowNav, useGridLayout } from './gridHooks';
import {
  buildPuzzleData, computeClaimedCells, findWords, generatorStart, initialGridState, pasteIntoGrid, resizeGrid, validatePuzzle, wordCells,
} from './gridModel';

// generatedGrid: rows of a grid picked in the generator ('■' = black); replaces the day's grid unsaved.
const GridEditor = ({ puzzle, puzzles = [], onSave, onBack, onDelete, onNavigate, onGenerate, generatedGrid }) => {
  const [grid, setGrid] = useState([]);
  const [words, setWords] = useState([]);
  const [clues, setClues] = useState({});
  const [size, setSize] = useState({ width: 6, height: 7 });
  const [unsaved, setUnsaved] = useState(false);
  const [activeCell, setActiveCell] = useState({ row: 0, col: 0 });
  const [activeWordId, setActiveWordId] = useState(null);
  const [hoverWordId, setHoverWordId] = useState(null);
  // Clue field that has focus right now (activeWordId outlives blur for the dropdown).
  const [focusedWordId, setFocusedWordId] = useState(null);
  // Clue text when the active word was focused; typing past it becomes the desk's search query.
  const [focusBaseline, setFocusBaseline] = useState('');
  const [deskOpen, setDeskOpen] = useState(false);
  // Which clue field has the word: the clue list, or the stepper under the grid (narrow screens).
  const [fieldSource, setFieldSource] = useState('list');
  const [emojiTarget, setEmojiTarget] = useState(null); // { wordId, input, cursor }
  const { centerRef, stacked, cellSize } = useGridLayout(size.width + 1); // stacked: single-column layout
  const stepperRef = React.useRef(null);
  const stepperInputRef = React.useRef(null);
  const [isSpecialDate, setIsSpecialDate] = useState(
    !!(puzzle?.specialDate || puzzle?.specialDateNotification)
  );
  const [specialTitle, setSpecialTitle] = useState(puzzle?.specialDate?.title || '');
  const [specialName, setSpecialName] = useState(puzzle?.specialDate?.name || '');
  const [notificationTitle, setNotificationTitle] = useState(puzzle?.specialDateNotification?.title || '');
  const [notificationBody, setNotificationBody] = useState(puzzle?.specialDateNotification?.body || '');
  const textareaRefs = React.useRef({});

  const initGrid = useCallback(() => {
    const initial = initialGridState(puzzle, generatedGrid);
    setGrid(initial.grid);
    setSize(initial.size);
    setClues(initial.clues);
    if (initial.unsaved) setUnsaved(true);

    // Special date fields always come from this puzzle, never from the previous one.
    setIsSpecialDate(!!(puzzle?.specialDate || puzzle?.specialDateNotification));
    setSpecialTitle(puzzle?.specialDate?.title || '');
    setSpecialName(puzzle?.specialDate?.name || '');
    setNotificationTitle(puzzle?.specialDateNotification?.title || '');
    setNotificationBody(puzzle?.specialDateNotification?.body || '');
  }, [puzzle, generatedGrid]);

  const detectWords = useCallback(() => {
    const found = findWords(grid, clues, size);
    if (found) setWords(found);
  }, [grid, clues, size]);

  useEffect(() => initGrid(), [initGrid]);
  useEffect(() => detectWords(), [detectWords]);

  const claimedCells = React.useMemo(() => computeClaimedCells(size, grid, words, clues), [size, grid, words, clues]);

  useCellArrowNav(activeCell, setActiveCell, size);

  const updateCell = (row, col, value) => {
    const validChars = /^[QWERTYUIOPÅ…ASDFGHJKLÖ–ÄZXCVBNM]$/i;
    if (value && !validChars.test(value)) return;
    
    const newGrid = grid.map((r, rIdx) => 
      r.map((c, cIdx) => 
        rIdx === row && cIdx === col ? { ...c, letter: value.toUpperCase() } : c
      )
    );
    setGrid(newGrid);
    setUnsaved(true);
  };

  const toggleBlack = (row, col) => {
    const newGrid = grid.map((r, rIdx) => 
      r.map((c, cIdx) => 
        rIdx === row && cIdx === col ? { ...c, isBlack: !c.isBlack, letter: c.isBlack ? c.letter : '' } : c
      )
    );
    setGrid(newGrid);
    setUnsaved(true);
  };

  const updateClue = (wordId, clue) => {
    setClues(prev => ({ ...prev, [wordId]: clue }));
    setUnsaved(true);
    if (wordId === activeWordId) setDeskOpen(true);
  };

  const insertSoftHyphen = (wordId, textareaRef) => {
    if (!textareaRef) return;

    const textarea = textareaRef;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentClue = clues[wordId] || '';

    // Insert placeholder soft hyphen at cursor position
    const newClue = currentClue.substring(0, start) + '︲' + currentClue.substring(end);

    setClues(prev => ({ ...prev, [wordId]: newClue }));
    setUnsaved(true);

    // Restore cursor position after the inserted character
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 1, start + 1);
    }, 0);
  };

  // Auto-place soft hyphens across the clue using the Finnish hyphenator.
  // Runs only on plain text outside {icon}/{c.icon}/{emoji} braces, replaces
  // any existing placeholder hyphens, and stores them as the editor's ︲
  // placeholder (converted to U+00AD on save) — same format as the manual button.
  const autoHyphenate = (wordId) => {
    const currentClue = clues[wordId] || '';
    if (!currentClue.trim()) return;

    // Strip existing placeholder/real soft hyphens before re-hyphenating.
    const stripped = currentClue.replace(/[­︲]/g, '');

    // Hyphenate only the text outside {icon}/{c.icon}/{emoji} tokens. Split on
    // brace groups, keep the groups verbatim, hyphenate the rest.
    const hyphenated = stripped
      .split(/(\{[^{}]*\})/g)
      .map(part =>
        part.startsWith('{') ? part : hyphenateFinnish(part).replace(/­/g, '︲')
      )
      .join('');

    if (hyphenated === currentClue) return;
    setClues(prev => ({ ...prev, [wordId]: hyphenated }));
    setUnsaved(true);
  };

  const focusWord = (word, source = 'list') => {
    setFieldSource(source);
    setFocusedWordId(word.id);
    setDeskOpen(true);
    if (word.id === activeWordId) return;
    setActiveWordId(word.id);
    setFocusBaseline(clues[word.id] || '');
  };

  const closeDesk = useCallback(() => setDeskOpen(false), []);

  // A suggestion replaces the whole clue, then the caret goes back to the field.
  const applySuggestion = (value) => {
    if (activeWordId == null) return;
    setClues(prev => ({ ...prev, [activeWordId]: value }));
    setUnsaved(true);
    setFocusBaseline(value);
    setDeskOpen(false);
    setTimeout(() => {
      const input = fieldFor(activeWordId);
      if (!input) return;
      input.focus();
      input.setSelectionRange(value.length, value.length);
    }, 0);
  };

  const fieldFor = (wordId) => (fieldSource === 'stepper' ? stepperInputRef.current : textareaRefs.current[wordId]);

  // Emoji go in at the caret the field had when the picker opened.
  const openEmoji = (wordId, input) => {
    if (!input) return;
    setEmojiTarget({ wordId, input, cursor: input.selectionStart ?? (clues[wordId] || '').length });
  };
  const closeEmoji = useCallback(() => setEmojiTarget(null), []);
  const insertEmoji = (emoji) => {
    const { wordId, input, cursor } = emojiTarget;
    const current = clues[wordId] || '';
    updateClue(wordId, current.slice(0, cursor) + emoji + current.slice(cursor));
    setEmojiTarget(null);
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(cursor + emoji.length, cursor + emoji.length);
    }, 100);
  };

  const paste = text => {
    const newGrid = pasteIntoGrid(grid, text);
    if (!newGrid) return;
    setGrid(newGrid);
    setUnsaved(true);
  };

  const isPastOrToday = () => {
    const puzzleDate = new Date(puzzle.id.split('-').reverse().join('-'));
    puzzleDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return puzzleDate <= today;
  };

  const deletePuzzle = async () => {
    const message = `Are you sure you want to delete the puzzle for ${puzzle.id}?\n\nThis action cannot be undone.`;
    if (!window.confirm(message)) return;
    await onDelete(puzzle.id);
  };

  const moveCrossword = async () => {
    const targetDate = window.prompt(`Move this crossword to which date? (format: dd-mm-yyyy)\n\nCurrent: ${puzzle.id}`);
    if (!targetDate) return;

    if (!/^\d{2}-\d{2}-\d{4}$/.test(targetDate)) {
      alert('Invalid date format. Use dd-mm-yyyy (e.g., 25-12-2024)');
      return;
    }

    try {
      const success = await moveCrosswordData(puzzle.id, targetDate);
      if (success) {
        alert(`Crossword moved to ${targetDate}`);
        onBack();
      }
    } catch (error) {
      alert(`Failed to move crossword: ${error.message}`);
    }
  };

  const navigateDay = (direction) => {
    // Parse current date (dd-mm-yyyy)
    const [day, month, year] = puzzle.id.split('-').map(Number);
    const currentDate = new Date(year, month - 1, day);

    // Add or subtract one day
    currentDate.setDate(currentDate.getDate() + direction);

    // Format new date
    const newDateStr = formatDate(currentDate);

    // Save current puzzle if there are unsaved changes
    if (unsaved) {
      if (window.confirm('You have unsaved changes. Save before navigating?')) {
        save().then(() => {
          if (onNavigate) onNavigate(newDateStr);
        });
        return;
      }
    }

    if (onNavigate) onNavigate(newDateStr);
  };

  const save = async () => {
    const errors = validatePuzzle(words, grid, clues);
    
    if (errors.length) {
      const message = 'Puzzle has issues:\n\n' + errors.join('\n') + '\n\nSave anyway?';
      if (!window.confirm(message)) return;
    }
    
    if (isPastOrToday()) {
      const message = `This is ${puzzle.id === formatDate(new Date()) ? "today's" : "a past"} puzzle. Are you sure you want to modify it?`;
      if (!window.confirm(message)) return;
    }
    
    const data = buildPuzzleData({ puzzle, size, grid, words, clues, isSpecialDate, specialTitle, specialName, notificationTitle, notificationBody });

    await onSave(data);
    setUnsaved(false);
  };

  // Opens the generator with this grid (size, black squares, typed letters) as its starting point.
  const openGenerator = () => {
    if (unsaved && !window.confirm('You have unsaved changes. They will be lost. Open the generator anyway?')) return;
    onGenerate(generatorStart(grid, size));
  };

  const handleSizeChange = (dimension, value) => {
    const newGrid = resizeGrid(grid, size, dimension, value);
    setSize(prev => ({ ...prev, [dimension]: value }));
    setGrid(newGrid);
    setUnsaved(true);
  };


  const activeWord = words.find(w => w.id === activeWordId) || null;
  // Narrow screens keep the stepper's word lit while its field is blurred (emoji picker, etc.).
  const highlightId = hoverWordId ?? focusedWordId ?? (stacked ? activeWordId : null);

  // Clue order for the stepper: across, then down (as in the clue list).
  const orderedWords = [...words.filter(w => w.direction === 'across'), ...words.filter(w => w.direction === 'down')];
  const stepperWord = activeWord || orderedWords[0] || null;
  const stepWord = (delta) => {
    if (!orderedWords.length) return;
    const i = orderedWords.findIndex(w => w.id === stepperWord?.id);
    focusWord(orderedWords[(i + delta + orderedWords.length) % orderedWords.length], 'stepper');
  };
  // With the stepper's suggestions open the grid scrolls to the top of the screen (full size)
  // to leave them room below the field.
  const focusStepper = () => {
    if (stepperWord) focusWord(stepperWord, 'stepper');
    centerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const highlighted = wordCells(words.find(w => w.id === highlightId));
  const pastIndex = React.useMemo(() => buildPastClueIndex(puzzles, puzzle.id), [puzzles, puzzle.id]);
  const activeClue = activeWord ? clues[activeWord.id] || '' : '';
  const deskQuery = activeClue !== focusBaseline ? activeClue.replace(/︲/g, '') : '';

  const filledClues = words.filter(w => clues[w.id]?.trim()).length;
  const todayId = formatDate(new Date());
  const status = puzzle.id === todayId ? 'today'
    : isPastOrToday() ? 'past'
    : !words.length ? 'empty'
    : filledClues === words.length ? 'ready'
    : 'draft';
  const [dd, mm, yyyy] = puzzle.id.split('-').map(Number);
  const weekday = new Date(yyyy, mm - 1, dd).toLocaleDateString('en-GB', { weekday: 'long' });

  // Ctrl/Cmd+S saves, Alt+←/→ switches day. A ref keeps the handler on the latest closures.
  const shortcutRef = React.useRef();
  shortcutRef.current = e => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      save();
    } else if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      navigateDay(e.key === 'ArrowLeft' ? -1 : 1);
    }
  };
  useEffect(() => {
    const onKey = e => shortcutRef.current(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="editor-container">
      <EditorHeader
        onBack={onBack} navigateDay={navigateDay} weekday={weekday} dd={dd} mm={mm} yyyy={yyyy} status={status}
        moveCrossword={moveCrossword} deletePuzzle={deletePuzzle} save={save} unsaved={unsaved}
      />

      <div className="editor-content">
        <GridSidebar
          size={size}
          onSizeChange={handleSizeChange}
          onPaste={paste}
          onGenerate={onGenerate ? openGenerator : null}
          disabled={puzzle?.words?.length > 0}
          isSpecialDate={isSpecialDate}
          specialTitle={specialTitle}
          specialName={specialName}
          onSpecialDateChange={(checked) => {
            setIsSpecialDate(checked);
            setUnsaved(true);
          }}
          onSpecialTitleChange={(value) => {
            setSpecialTitle(value);
            setUnsaved(true);
          }}
          onSpecialNameChange={(value) => {
            setSpecialName(value);
            setUnsaved(true);
          }}
          notificationTitle={notificationTitle}
          notificationBody={notificationBody}
          onNotificationTitleChange={(value) => {
            setNotificationTitle(value);
            setUnsaved(true);
          }}
          onNotificationBodyChange={(value) => {
            setNotificationBody(value);
            setUnsaved(true);
          }}
        />

        <div className="editor-center" ref={centerRef}>
          <CrosswordGrid
            size={size} grid={grid} words={words} clues={clues} claimedCells={claimedCells} gridCell={cellSize}
            highlighted={highlighted} highlightId={highlightId} hoverWordId={hoverWordId}
            toggleBlack={toggleBlack} updateCell={updateCell} setActiveCell={setActiveCell}
          />

          {stacked && (
            <ClueStepper
              ref={stepperRef}
              inputRef={stepperInputRef}
              words={orderedWords}
              word={stepperWord}
              clue={stepperWord ? clues[stepperWord.id] || '' : ''}
              suggestOpen={deskOpen && fieldSource === 'stepper'}
              onStep={stepWord}
              onChange={value => updateClue(stepperWord.id, value)}
              onFocus={focusStepper}
              onBlur={() => setFocusedWordId(null)}
              onRequestSuggest={focusStepper}
              onOpenEmoji={openEmoji}
              onInsertSoftHyphen={insertSoftHyphen}
              onAutoHyphenate={autoHyphenate}
            />
          )}
        </div>

        <CluesPanel
          words={words}
          clues={clues}
          activeWordId={activeWordId}
          pastIndex={pastIndex}
          onUpdateClue={updateClue}
          onInsertSoftHyphen={insertSoftHyphen}
          onAutoHyphenate={autoHyphenate}
          onOpenEmoji={openEmoji}
          deskOpen={deskOpen && fieldSource === 'list'}
          onRequestSuggest={word => focusWord(word)}
          onFocusWord={word => focusWord(word)}
          onBlurWord={() => setFocusedWordId(null)}
          onHoverWord={setHoverWordId}
          textareaRefs={textareaRefs}
        />
      </div>

      {deskOpen && activeWord && (
        <WordDesk
          word={activeWord}
          query={deskQuery}
          pastClues={pastIndex.get(activeWord.answer) || []}
          onApply={applySuggestion}
          anchor={fieldSource === 'stepper' ? stepperRef.current : textareaRefs.current[activeWord.id]?.closest('.word-item')}
          placement={fieldSource === 'stepper' ? 'below' : 'auto'}
          onClose={closeDesk}
        />
      )}

      {emojiTarget && <EmojiPickerDialog onSelect={insertEmoji} onClose={closeEmoji} />}
    </div>
  );
};

export default GridEditor;
